/**
 * SlamDebugHud — the on-device instrument panel.
 *
 * Everything you need to judge a tracking build at a glance while holding the
 * phone: state, features, latency, fps, drift, and live ship-gate pass/fail.
 * Plus one-tap recording of a dataset for the replay harness.
 *
 * Subscribes to telemetry directly and throttles its own repaint, so it never
 * re-renders the artwork.
 */

import { useEffect, useRef, useState } from 'react';
import { telemetry, type TelemetrySample } from '../spatial/telemetry.js';
import { motionPermissionState } from '../spatial/DevicePose.js';
import {
  cameraDiagonalFovDeg,
  setCameraDiagonalFovDeg,
  frameHorizontalFovDeg,
  viewGeometry,
} from '../spatial/cameraFov.js';
import { runtime } from '../session/runtime.js';
import { SlamRecorder } from '../capture/slamRecorder.js';
import { HAS_API } from '../config.js';
import { alvaLoadError } from '../spatial/AlvaARBackend.js';
import { xrSessionError } from '../spatial/WebXRBackend.js';

const REPAINT_MS = 250;

export function SlamDebugHud({ onClose }: { onClose: () => void }) {
  const [sample, setSample] = useState<TelemetrySample | null>(null);
  const [, setTick] = useState(0);
  const recorderRef = useRef<SlamRecorder | null>(null);
  const [recording, setRecording] = useState(false);
  const [frames, setFrames] = useState(0);
  const [upload, setUpload] = useState<{
    state: 'idle' | 'uploading' | 'done' | 'error';
    pct?: number;
    msg?: string;
  }>({ state: 'idle' });

  useEffect(() => {
    let last = 0;
    const unsub = telemetry.subscribe((s) => {
      const now = performance.now();
      if (now - last < REPAINT_MS) return;
      last = now;
      setSample(s);
    });
    const iv = window.setInterval(() => {
      setTick((t) => t + 1);
      const r = recorderRef.current;
      if (r?.isRecording) setFrames(r.frameCount);
    }, 500);
    return () => {
      unsub();
      window.clearInterval(iv);
    };
  }, []);

  // Closing the HUD (or leaving the field) ends the recording. Without this the
  // recorder's two intervals kept running, unreachable, for the page's lifetime
  // and kept appending frames and IMU samples to an array nothing could read.
  useEffect(
    () => () => {
      const r = recorderRef.current;
      if (r) {
        r.stop();
        r.discard();
      }
      recorderRef.current = null;
    },
    [],
  );

  const summary = telemetry.summary();
  const gates = telemetry.gates();
  const motionPerm = motionPermissionState();
  const scale = runtime.spatial.metricScale?.();
  const health = runtime.spatial.mapHealth?.();
  const sceneDepth = (
    runtime.spatial as { sceneDepthStatus?(): import('../spatial/SceneDepth.js').SceneDepthStatus }
  ).sceneDepthStatus?.();
  const jumps = (runtime.spatial as { rejectedJumps?(): number }).rejectedJumps?.() ?? 0;
  // AlvaAR starts a brand-new map (new frame, new scale) after a loss; the backend
  // now detects that and drops everything stored in the old map's units.
  const restarts = (runtime.spatial as { mapRestarts?(): number }).mapRestarts?.() ?? 0;
  const fov = cameraDiagonalFovDeg();
  // What the participant actually sees, after object-fit: cover crops the frame.
  const passthrough = document.querySelector<HTMLVideoElement>('video.passthrough');
  const view = viewGeometry(passthrough);
  const trackerFov = runtime.spatial.trackerFovDeg?.() ?? null;
  // The tracker sees the whole frame; the screen sees a crop of it, so `view.hFovDeg`
  // is NOT comparable (on a tall screen it is always far smaller). Compare the
  // tracker's FOV with what the CURRENT calibration implies for the same full
  // frame — dirty means the calibration changed since Initialize().
  const currentFrameFov =
    passthrough && passthrough.videoWidth > 0 && passthrough.videoHeight > 0
      ? frameHorizontalFovDeg(passthrough.videoWidth, passthrough.videoHeight)
      : null;
  const fovDirty =
    trackerFov !== null && currentFrameFov !== null && Math.abs(trackerFov - currentFrameFov) > 3;

  const nudgeFov = (delta: number) => {
    setCameraDiagonalFovDeg(cameraDiagonalFovDeg() + delta);
    setTick((t) => t + 1); // repaint immediately; the field picks it up next rAF
  };
  const hasFrames = (recorderRef.current?.frameCount ?? 0) > 0;

  const toggleRecord = () => {
    let rec = recorderRef.current;
    if (!rec) {
      try {
        rec = new SlamRecorder();
        recorderRef.current = rec;
      } catch {
        return;
      }
    }
    if (rec.isRecording) {
      rec.stop();
      setRecording(false);
    } else {
      const src = runtime.frameSource;
      const pose = runtime.devicePose;
      if (src && pose) {
        rec.start(src, pose);
        setRecording(true);
      }
    }
  };

  const exportDataset = () => {
    const rec = recorderRef.current;
    if (!rec) return;
    rec.stop();
    setRecording(false);
    rec.export();
  };

  /** Upload so it can be opened from the replay page on a desktop — no cable. */
  const uploadDataset = async () => {
    const rec = recorderRef.current;
    if (!rec || rec.frameCount === 0) return;
    rec.stop();
    setRecording(false);
    setUpload({ state: 'uploading', pct: 0 });
    const res = await rec.upload({
      onProgress: (f) => setUpload({ state: 'uploading', pct: f }),
    });
    if ('error' in res) {
      setUpload({ state: 'error', msg: res.error });
    } else {
      setUpload({ state: 'done', msg: `${(res.bytes / 1024 / 1024).toFixed(1)}MB` });
    }
  };

  const stateColor =
    sample?.state === 'tracking'
      ? '#7ee0b8'
      : sample?.state === 'lost'
        ? '#e0574b'
        : sample?.state === 'initializing'
          ? '#e0a341'
          : '#9aa0b4';

  return (
    <div className="slam-hud">
      <div className="slam-hud-head">
        <strong>SLAM · {summary.backend}</strong>
        <button className="slam-x" onClick={onClose}>
          ✕
        </button>
      </div>
      {/* Falling back to VO is a SILENT, total loss of world-locking, and it is
          easy to miss a three-letter word in a header. VO is dead reckoning: no
          map, no landmarks, no relocalisation — fragments cannot stick to the
          room because there is no room model to stick to.
          Two distinct causes, and they used to be indistinguishable: the file is
          missing (gitignored — the normal state of a deploy that never fetched
          it), OR the file is present but threw while loading (WASM init failure,
          COOP/COEP misconfigured, a stale cached response). `alvaLoadError()`
          only has a value in the second case — show it verbatim when present. */}
      {summary.backend !== 'alva' && summary.backend !== 'webxr' && (
        <div className="slam-warn">
          no SLAM — {summary.backend} is dead reckoning, nothing world-locks.
          <br />
          {xrSessionError() ? (
            <>AR session refused: {xrSessionError()}</>
          ) : alvaLoadError() ? (
            <>alva_ar.js present but failed: {alvaLoadError()}</>
          ) : (
            <>
              /vendor/alva_ar.js missing? run <code>npm run vendor:slam</code>
            </>
          )}
        </div>
      )}

      <div className="slam-row">
        <span>state</span>
        <b style={{ color: stateColor }}>{sample?.state ?? 'idle'}</b>
      </div>
      <div className="slam-row">
        <span>features</span>
        <b>{sample?.featureCount ?? 0}</b>
      </div>
      <div className="slam-row">
        <span>track ms</span>
        <b>
          {(sample?.trackMs ?? 0).toFixed(1)} <i>p95 {summary.p95TrackMs.toFixed(0)}</i>
        </b>
      </div>
      <div className="slam-row">
        <span>fps</span>
        <b>{(sample?.fps ?? 0).toFixed(1)}</b>
      </div>
      <div className="slam-row">
        <span>yaw / pitch</span>
        <b>
          {(sample?.yaw ?? 0).toFixed(0)}° / {(sample?.pitch ?? 0).toFixed(0)}°
        </b>
      </div>
      <div className="slam-row">
        <span>pos</span>
        <b>
          {sample?.pos
            ? sample.pos.map((v) => v.toFixed(2)).join(', ')
            : '—'}
        </b>
      </div>
      <div className="slam-row">
        <span>uptime</span>
        <b>{(summary.trackingUptime * 100).toFixed(0)}%</b>
      </div>
      <div className="slam-row">
        <span>resets / lost</span>
        <b>
          {summary.resets} / {summary.lostEvents}
        </b>
      </div>
      {/* A denied motion prompt is otherwise completely silent: tracking looks
          fine because AlvaAR is vision-only, and you only find out when the
          dataset comes back with 900 identity quaternions. */}
      <div className="slam-row">
        <span>imu</span>
        <b className={motionPerm === 'granted' ? 'ok' : 'bad'}>{motionPerm}</b>
      </div>
      {/* Map trustworthiness, distinct from tracking state: AlvaAR reports
          healthy tracking while teleporting between duplicate map regions.
          `jumps` counts poses vetoed as physically impossible — a non-zero and
          climbing number means the map is contradicting itself right now. */}
      {health && (
        <div className="slam-row">
          <span>map</span>
          <b className={health.phase === 'healthy' ? 'ok' : health.phase === 'lost' ? 'bad' : ''}>
            {health.phase}
            {health.phase === 'mapping' ? ` ${Math.round(health.progress * 100)}%` : ''}
            {jumps > 0 ? ` · ${jumps} jumps` : ''}
            {restarts > 0 ? ` · ${restarts} restarts` : ''}
          </b>
        </div>
      )}
      {/* How much text is actually pinned to the room, and whether it has
          settled or is still waiting on depth — see AlvaARBackend's
          StoredAnchor note. `total > 0` with `timeToFirstAnchorMs` staying
          null on a fresh run would mean fragments exist but nothing is calling
          anchor() on them: a wiring fault upstream of tracking, not a tracking
          one, and this is the only place that distinguishes the two. */}
      {summary.finalAnchors && (
        <div className="slam-row">
          <span>anchors</span>
          <b className={summary.finalAnchors.total === 0 ? 'bad' : ''}>
            {summary.finalAnchors.total === 0
              ? 'none placed'
              : `${summary.finalAnchors.committed}/${summary.finalAnchors.total} settled`}
            {summary.timeToFirstAnchorMs !== null
              ? ` · first @ ${(summary.timeToFirstAnchorMs / 1000).toFixed(1)}s`
              : ''}
          </b>
        </div>
      )}
      {/* Metric grounding. `scale` is metres per SLAM unit; `height` is what the
          floor plane says the camera is at, which should read ~1.00 of the
          assumed height once locked — hold the phone at normal walking height
          and check. A value that keeps moving means the map is drifting. */}
      {/* WebXR was advertised as supported but the session did not open, and we
          fell back. Worth showing even though tracking now works, because the
          cause is usually actionable — most often Google Play Services for AR
          needing an install or update on Android. */}
      {xrSessionError() && summary.backend !== 'webxr' && (
        <div className="slam-row">
          <span>webxr</span>
          <b className="bad">failed · {xrSessionError()}</b>
        </div>
      )}
      {/* Scene depth in SLAM units — what placement actually uses when metric
          scale is unknown, which is most sessions. If this stays '—' fragments
          are being planted at the raw constant, which is the failure mode that
          left text half a metre from the participant's face. */}
      {sceneDepth && (
        <div className="slam-row">
          <span>room</span>
          <b className={sceneDepth.depth != null ? 'ok' : ''}>
            {sceneDepth.depth != null
              ? `${sceneDepth.depth.toFixed(2)} u · anchor ${(sceneDepth.depth * 0.8).toFixed(2)} u (${sceneDepth.estimates}) · ${sceneDepth.cloud}pts`
              : /* Two very different failures, previously indistinguishable.
                   No estimates at all means triangulation is not succeeding —
                   stand still, or too few features. Estimates piling up with a
                   spread that will not come down means the measurements
                   disagree, which is the state that used to be reported as a
                   confident depth and planted text 4 cm from the lens. */
                `${sceneDepth.estimates === 0 ? 'measuring' : 'disagreeing'}… (${sceneDepth.estimates}${
                  sceneDepth.spread != null ? ` ±${(sceneDepth.spread * 100).toFixed(0)}%` : ''
                })`}
          </b>
        </div>
      )}
      {scale && (
        <div className="slam-row">
          <span>scale</span>
          <b className={scale.state === 'locked' ? 'ok' : scale.state === 'rejected' ? 'bad' : ''}>
            {scale.metersPerUnit
              ? `${scale.metersPerUnit.toFixed(3)} m/u · ${scale.state} (${scale.samples})`
              : `${scale.state} (${scale.samples})`}
          </b>
        </div>
      )}

      {/* Camera geometry. The stored number is the DIAGONAL fov, because that is
          what a phone's spec sheet publishes (Oppo main camera: 76°). Everything
          else is derived: the tracker gets the frame's full horizontal fov, the
          renderer gets what survives object-fit: cover, which on a tall screen is
          much narrower horizontally and much wider vertically.
          Too high → fragments lag behind the scene when turning; too low → they
          overshoot. Persisted across sessions. */}
      <div className="slam-row">
        <span>fov ⌀</span>
        <span className="slam-fov">
          <button onClick={() => nudgeFov(-2)} aria-label="decrease field of view">
            −
          </button>
          <b>{fov.toFixed(0)}°</b>
          <button onClick={() => nudgeFov(2)} aria-label="increase field of view">
            +
          </button>
        </span>
      </div>
      <div className="slam-row">
        <span>view h/v</span>
        <b>
          {view.hFovDeg.toFixed(0)}° / {view.vFovDeg.toFixed(0)}°
        </b>
      </div>
      {fovDirty && (
        <div className="slam-note">tracker on {trackerFov?.toFixed(0)}° — tap reset to re-apply</div>
      )}

      <div className="slam-gates">
        {gates.map((g) => (
          <span key={g.name} className={g.pass ? 'gate pass' : 'gate fail'}>
            {g.name.replace(/Ms$/, '')} {g.pass ? '✓' : '✕'}
          </span>
        ))}
      </div>

      <div className="slam-actions">
        <button onClick={toggleRecord} className={recording ? 'rec active' : 'rec'}>
          {recording ? `● rec ${frames}` : 'record'}
        </button>
        <button onClick={() => void uploadDataset()} disabled={!hasFrames || !HAS_API}
          title={HAS_API ? 'send to server for desktop replay' : 'no server configured'}>
          upload
        </button>
        <button onClick={exportDataset} disabled={!hasFrames}>
          save
        </button>
        <button onClick={() => runtime.spatial.resetTracking?.()}>reset</button>
      </div>

      {upload.state !== 'idle' && (
        <div className={`slam-upload ${upload.state}`}>
          {upload.state === 'uploading'
            ? `uploading ${Math.round((upload.pct ?? 0) * 100)}%`
            : upload.state === 'done'
              ? `uploaded ✓ ${upload.msg} — open /replay.html`
              : `upload failed: ${upload.msg}`}
        </div>
      )}
    </div>
  );
}
